import React from "react";
import { Bookmark } from "lucide-react";
import { Card, CardContent } from "../ui/card";

// TopSavedLeaderboard — surfaces the producer's top 3 most-saved
// videos on their dashboard. The flip-side of the engagement loop:
// viewers save → producer sees concrete proof of what resonates.
//
// Self-hides when no video has been saved by anyone — avoids
// demotivating new producers with an empty leaderboard. Once even one
// save exists, the panel renders to teach the producer that this is a
// signal they can track.
//
// Pure presentation: receives the producer's own videos array (from
// DashboardPage state) and slices the top 3 by `trail_count`. No
// backend call — the data is already on the videos doc.
const TopSavedLeaderboard = ({ videos }) => {
  const top = (videos || [])
    .filter(v => (v.trail_count || 0) > 0)
    .sort((a, b) => (b.trail_count || 0) - (a.trail_count || 0))
    .slice(0, 3);

  if (top.length === 0) return null;

  return (
    <Card className="bg-gray-900 border-gray-800 mb-6" data-testid="top-saved-leaderboard">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-3">
          <Bookmark className="h-4 w-4 text-amber-400 fill-amber-400" />
          <h3 className="text-white font-semibold text-sm">Your most-saved videos</h3>
          <span className="text-gray-500 text-xs">· what viewers love</span>
        </div>
        <div className="space-y-2">
          {top.map((v, i) => (
            <div
              key={v.id}
              className="flex items-center gap-3 px-3 py-2 rounded-lg bg-gray-800/50 hover:bg-gray-800/80 transition-colors"
              data-testid={`top-saved-row-${i}`}
            >
              <span className={`text-2xl font-bold tabular-nums w-7 text-center ${
                i === 0 ? 'text-amber-300' : i === 1 ? 'text-gray-400' : 'text-orange-500/70'
              }`}>
                #{i + 1}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium leading-tight truncate">
                  {v.title}
                </p>
                <p className="text-gray-500 text-[11px] leading-tight">
                  {v.location?.city || "Unknown"}{v.location?.country ? `, ${v.location.country}` : ""}
                </p>
              </div>
              <span className="flex items-center gap-1 text-amber-300 text-sm font-semibold tabular-nums shrink-0">
                <Bookmark className="h-3.5 w-3.5 fill-amber-300" />
                {v.trail_count}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default TopSavedLeaderboard;
